import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "@/redux/axios";

// History Types
export interface HistoryItem {
  id: string;
  evaluation_id: string;
  file_name: string;
  date: string;
  score: number;
  total_questions: number;
  total_score?: number;
  max_score?: number;
  exam_type?: string;
  subject?: string;
  duration?: string;
  subjects?: { name: string; score: number }[];
}

interface HistoryState {
  history: HistoryItem[];
  isFetching: boolean;
  isError: boolean;
  errorMessage: string;
}

const initialState: HistoryState = {
  history: [],
  isFetching: false,
  isError: false,
  errorMessage: "",
};

// Fetch evaluation history with optional filter
export const fetchHistory = createAsyncThunk(
  "history/fetchHistory",
  async (historyType: string | undefined, thunkAPI) => {
    try {
      console.log("🚀 Fetching evaluation history...", historyType ? `Filter: ${historyType}` : "All");

      // Build URL with optional history_type parameter
      const url = historyType && historyType !== "All Papers"
        ? `/analytics/evaluationHistory?history_type=${historyType}`
        : "/analytics/evaluationHistory";

      const response = await axios.get(url);

      console.log("✅ History response:", response.data);

      if (response.status === 200) {
        return response.data;
      } else {
        return thunkAPI.rejectWithValue(response.data);
      }
    } catch (err: any) {
      console.error("❌ Error fetching history:", err);
      if (!err.response) {
        console.error("Error:", err);
        throw err;
      }
      return thunkAPI.rejectWithValue(
        err.response.data || { message: "Failed to fetch history" }
      );
    }
  }
);

// Fetch single evaluation by ID
export const fetchEvaluationById = createAsyncThunk(
  "history/fetchEvaluationById",
  async (evaluationId: string, thunkAPI) => {
    try {
      const response = await axios.get(`/evaluations/${evaluationId}`);

      if (response.status === 200) {
        return response.data;
      } else {
        return thunkAPI.rejectWithValue(response.data);
      }
    } catch (err: any) {
      if (!err.response) {
        console.error("Error:", err);
        throw err;
      }
      return thunkAPI.rejectWithValue(
        err.response.data || { message: "Failed to fetch evaluation" }
      );
    }
  }
);

export const historySlice = createSlice({
  name: "history",
  initialState,
  reducers: {
    clearHistory: (state) => {
      state.history = [];
      state.isError = false;
      state.errorMessage = "";
    },
    clearError: (state) => {
      state.isError = false;
      state.errorMessage = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch History
      .addCase(fetchHistory.pending, (state) => {
        state.isFetching = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(fetchHistory.fulfilled, (state, action) => {
        state.isFetching = false;
        // Map API response structure to HistoryItem format
        const evaluations = action.payload?.evaluations || [];
        state.history = evaluations.map((item: any) => ({
          id: item.evaluation_id,
          evaluation_id: item.evaluation_id,
          file_name: item.title || "Evaluation",
          date: item.date,
          score: item.score,
          total_questions: item.subjects?.length || 0,
          total_score: item.score,
          max_score: item.maxScore,
          exam_type: item.exam_type,
          subject: item.title,
          subjects: item.subjects,
        }));
        state.isError = false;
      })
      .addCase(fetchHistory.rejected, (state, action: any) => {
        state.isFetching = false;
        state.isError = true;
        state.errorMessage =
          action.payload?.message || "Failed to fetch history";
      });
  },
});

export const { clearHistory, clearError } = historySlice.actions;

export const historySelector = (state: any) => state.history;

export const historyReducer = historySlice.reducer;
export default historySlice.reducer;
